import {useOrderListQuery, useOrderStatusGetQuery} from "../../app/api/api";
import {ColumnDef, FilterDef, FilterOperator, FilterType, ListRequestIdModelTable} from "../../components/ExTable";

import {useTranslation} from "react-i18next";
import {dateTimeColumnDef, defaultColumnDef} from "../../components/ExTable/variables";
import {ordinaryDetailPageLinkTo} from "../../utils/ordinaryDetailPageLink";
import Page from "../../components/Layout/Page";
import {OrderField, OrderFlat, OrderStatus} from "../../app/api/model/order";
import {RoutePath} from "../../types";
import {Typography} from "@mui/material";
import {Colors} from "../../components/Layout/Colors";
import {getTranslationKey, TranslationContext} from "../../utils/getTranslationKey";
import {lowerCase} from "lodash";
import {getStatusLabel} from "./helpers";
import React from "react";
import {getDateFilterDefinitions} from "../../utils/getDateFilterDefinitions";
import {CustomerNameField} from "./components/CustomerNameField";

export default function OrderList() {
    const {t} = useTranslation();
    const {data: orderStatus} = useOrderStatusGetQuery();

    const columnDefs: ColumnDef<OrderFlat>[] = [
        {
            fieldName: OrderField.ID,
            label: t("generic_id"),
            width: 80,
            generateLink: ordinaryDetailPageLinkTo(RoutePath.ORDERS),
            sticky: true,
            ...defaultColumnDef,
        },
        {
            fieldName: OrderField.STATUS,
            align: "center",
            label: t("generic_status"),
            sortable: true,
            width: 180,
            formatCellText: ({row}) => getStatusLabel(row[OrderField.STATUS], t, TranslationContext.ORDER_STATUS),
        },
        {
            fieldName: OrderField.LEGACY,
            align: "left",
            label: t("generic_legacy"),
            width: 100,
            sortable: true,
            formatCellText: ({row}) => (row[OrderField.LEGACY] ? t("generic_yes") : t("generic_no")),
        },
        {
            fieldName: OrderField.ORDER_NUMBER,
            label: t("order_orderNumber"),
            ...defaultColumnDef,
        },
        {
            ...defaultColumnDef,
            fieldName: OrderField.CUSTOMER_NUMBER,
            formatCellText: (param) =>
                !param.row.organizationId
                    ? {
                          node: <Typography color={Colors.RED}>{param.cellValue}</Typography>,
                      }
                    : {
                          node: <Typography>{param.cellValue}</Typography>,
                      },
            label: t("generic_customerNumber"),
        },
        {
            fieldName: OrderField.CUSTOMER_NAME,
            label: t("generic_customerName"),
            formatCellText: (param) => ({
                node: (
                    <CustomerNameField
                        customerName={param.cellValue}
                        customerNameMismatch={param.row[OrderField.CUSTOMER_NAME_MISMATCH]}
                        validationError={param.row[OrderField.VALIDATION_ERROR]}
                    />
                ),
            }),
            ...defaultColumnDef,
        },
        {
            ...defaultColumnDef,
            fieldName: OrderField.SHIPPING_RECIPIENT_NAME,
            label: t("order_shippingRecipientName"),
        },
        {
            ...defaultColumnDef,
            fieldName: OrderField.ADMIN_USER_EMAIL,
            label: t("order_adminUserEmail"),
        },
        {
            fieldName: OrderField.CREATED_AT,
            label: t("order_deliveredByDkvAt"),
            ...dateTimeColumnDef,
        },
        {
            fieldName: OrderField.SHIPMENT_CREATED_AT,
            label: t("generic_shipmentCreatedAt"),
            ...dateTimeColumnDef,
        },
        {
            fieldName: OrderField.CANCELED_AT,
            label: t("generic_canceledAt"),
            ...dateTimeColumnDef,
        },
        {
            ...dateTimeColumnDef,
            fieldName: OrderField.UPDATED_AT,
            label: t("generic_updatedAt"),
        },
    ];

    const filterDefs: FilterDef<OrderFlat>[] = [
        {
            fieldName: OrderField.STATUS,
            type: FilterType.ENUM,
            filterOperators: [FilterOperator.EQ, FilterOperator.NOT],
            enumValues: (orderStatus || Object.keys(OrderStatus)).map((status: string) => {
                return {
                    value: status,
                    labelKey: getTranslationKey({
                        context: TranslationContext.ORDER_STATUS,
                        value: lowerCase(status),
                    }),
                };
            }),
        },
        {
            fieldName: OrderField.LEGACY,
            filterOperators: [FilterOperator.EQ],
            type: FilterType.ENUM,
            enumValues: [
                {value: "true", labelKey: "generic_yes"},
                {value: "false", labelKey: "generic_no"},
            ],
        },
        {
            fieldName: OrderField.ORDER_NUMBER,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ],
        },
        {
            fieldName: OrderField.CUSTOMER_NAME,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ],
        },
        {
            fieldName: OrderField.CUSTOMER_NUMBER,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ],
        },
        {
            fieldName: OrderField.SHIPPING_RECIPIENT_NAME,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ],
        },
        {
            fieldName: OrderField.ADMIN_USER_EMAIL,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ],
        },
        ...getDateFilterDefinitions<OrderFlat>([
            OrderField.CANCELED_AT,
            OrderField.SHIPMENT_CREATED_AT,
            OrderField.CREATED_AT,
        ]),
    ];

    return (
        <Page>
            <ListRequestIdModelTable
                dataTest="ordersList"
                queryHook={useOrderListQuery}
                columnDefs={columnDefs}
                filterDefs={filterDefs}
                title={t("generic_orders")}
            />
        </Page>
    );
}
